import { Country } from 'country-state-city';
import { EAddressType } from '@xeppt/xeppt-sdk/types/user';
import { ECardStatus, ECardType } from '@xeppt/xeppt-sdk/types/card';
import {
    ETransactionAction,
    ETransactionMethod,
    ETransactionStatus,
    ETransactionType,
    TTransaction,
    TTransactionLite
} from '@xeppt/xeppt-sdk/types/transaction';
import * as XLSX from 'xlsx';
import moment from 'moment-timezone';
import {
    EGiftCardStatus,
    ETicketStatus,
    occupation,
    TNotification,
    TTicketMessage
} from '@xeppt/xeppt-sdk/types';
import { months } from '@const/dates';
import { EScheduleFrequency, EScheduleStatus } from '@xeppt/xeppt-sdk/types/schedule';
import { ELoyaltyLevel } from '@xeppt/xeppt-sdk/types/loyalty';
import { showToast } from '@components/toast';
import { EFundsOperation } from '@enum';
import { EPaymentIntentStatus, TPaymentIntent } from '@xeppt/xeppt-sdk/types/payment-intent';

export const getFirstLetter = (string?: string) => {
    if (!string) return '';
    return string.charAt(0);
};

export const hideCardNumber = (value?: number | string, type?: 'full' | 'partial') => {
    const separator = '••••';
    const string = String(value);
    if (type === 'full') {
        return `${separator} ${string}`;
    } else {
        return `${separator} ${separator} ${separator} ${string}`;
    }
};

export const prettifyStars = (value?: number | string) => {
    if (!value) return '';
    return String(value).replaceAll('*', '•');
};

export const prettifyCardStars = (value?: number | string) => {
    return String(value)
        .replace(' ', '')
        .replaceAll('*', '•')
        .replace(/(.{4})/g, '$1 ');
};

export const capitalizeFirstLetter = (string: string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
};

export const hidePhoneNumber = (value: string | number) => {
    const str = String(value);
    return (
        str
            .substring(0, str.length - 4)
            .split('')
            .map(() => '•')
            .join('') + str.substring(str.length - 4, str.length)
    );
};

export const joiningArrayWords = (array: string[]) => {
    if (array.length === 0) {
        return '';
    } else if (array.length === 1) {
        return array[0];
    } else {
        const joinedFormats = array.slice(0, -1).join(', ');
        return `${joinedFormats} or ${array[array.length - 1]}`;
    }
};

export const getBirthDisabledDates = (): Date => {
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() - 18);

    return maxDate;
};

export const getDisablePast = (): Date => {
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear());

    return maxDate;
};

export const getDisableFeature = (): Date => {
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear());

    return maxDate;
};

export const prettifyGoogleAddress = ({ address_components }: any) => {
    let streetName = '';
    let streetNumber = '';
    const address = {
        country: '',
        city: '',
        address_1: '',
        address_2: '',
        zip_code: '',
        region: ''
    };

    for (const component of address_components) {
        if (component.types.includes('street_number')) {
            streetNumber = component.long_name;
        }
        if (component.types.includes('route')) {
            streetName = component.long_name;
        }
        if (component.types.includes('locality')) {
            address['city'] = component.long_name;
        }
        if (component.types.includes('country')) {
            const country = Country.getCountryByCode(component.short_name);
            address['country'] = country?.name || component.short_name;
        }
        if (component.types.includes('postal_code')) {
            address['zip_code'] = component.long_name;
        }
        if (component.types.includes('administrative_area_level_1')) {
            address['region'] += component.long_name;
        }
    }
    if (
        address.country === '' ||
        address.city === '' ||
        streetName === '' ||
        streetNumber === '' ||
        address.zip_code === '' ||
        address.region === ''
    ) {
        return undefined;
    } else {
        return {
            ...address,
            address_1: streetNumber + ' ' + streetName
        };
    }
};

export const getAddressFormName = (type: EAddressType) => {
    console.log(type);
    if (type === EAddressType.BUSINESS) {
        return 'business_address';
    } else if (type === EAddressType.REGISTRATION) {
        return 'registration_address';
    } else if (type === EAddressType.BILLING) {
        return 'billing_address';
    } else {
        return 'trading_address';
    }
};

export const getAddressName = (type: EAddressType, localization: (val: string) => string) => {
    if (type === EAddressType.BUSINESS) {
        return localization('business');
    } else if (type === EAddressType.REGISTRATION) {
        return localization('registration');
    } else if (type === EAddressType.BILLING) {
        return localization('billing');
    } else {
        return localization('trading');
    }
};

export const prettifyCardStatus = (status: ECardStatus) => {
    switch (status) {
        case ECardStatus.ACTIVE:
            return 'active';
        case ECardStatus.TEMPORARY_CLOSED:
            return 'Pending activation';
        case ECardStatus.WAITING_FOR_ACTIVATION:
            return 'waiting for activation';
        case ECardStatus.LOST:
            return 'lost';
        case ECardStatus.STOLEN:
            return 'stolen';
        case ECardStatus.OTHER:
            return 'other';
        case ECardStatus.FRAUD:
            return 'fraud';
        case ECardStatus.CUSTOMER_REQUEST:
            return 'requested';
        case ECardStatus.CLOSED:
            return 'closed';
        case ECardStatus.LEGAL_CLOSED:
            return 'legal closed';
        case ECardStatus.FREEZED:
            return 'frozen';
    }
};

export const prettifyCardType = (status?: ECardType) => {
    switch (status) {
        case ECardType.PHYSICAL:
            return 'primary';
        case ECardType.VIRTUAL:
            return 'virtual';
        case ECardType.VIRTUAL_SUPPLEMENTARY:
            return 'Supplementary';
        case ECardType.PHYSICAL_SUPPLEMENTARY:
            return 'Supplementary';
        default:
            return 'primary';
    }
};

export const getTransactionAction = (
    action: ETransactionAction,
    method: ETransactionMethod,
    type: ETransactionType,
    t: (val: string) => string
): string => {
    if (method === ETransactionMethod.E_TRANSFER) {
        if (type === ETransactionType.CREDIT) {
            return t('send_money');
        } else {
            return t('request_money');
        }
    }
    if (method === ETransactionMethod.EPS) {
        if (type === ETransactionType.CREDIT) {
            return t('refund');
        } else {
            return t('load_balance');
        }
    }
    if (method === ETransactionMethod.EFT) {
        if (type === ETransactionType.CREDIT) {
            return t('send_money');
        } else {
            return t('load_balance');
        }
    }
    if (method === ETransactionMethod.PAY_BILL) {
        if (type === ETransactionType.CREDIT) {
            return t('bill_payment');
        }
    }
    if (method === ETransactionMethod.WALLET) {
        if (type === ETransactionType.CREDIT) {
            switch (action) {
                case ETransactionAction.REQUEST:
                    return t('request_money');
                case ETransactionAction.TRANSFER:
                    return t('send_money');
                case ETransactionAction.CARD_DEPOSIT:
                    return t('load_card');
                case ETransactionAction.CARD_SERVICE:
                    return t('request_card');
                case ETransactionAction.REFUND:
                    return t('refund');
            }
        } else {
            switch (action) {
                case ETransactionAction.REQUEST:
                    return t('request_money');
                case ETransactionAction.TRANSFER:
                    return t('receive_money');
                case ETransactionAction.CASHBACK:
                    return t('cashback');
                case ETransactionAction.REFUND:
                    return t('refund');
            }
        }
    }

    return '';
};

export const getTagVariant = (status: ECardStatus) => {
    switch (status) {
        case ECardStatus.ACTIVE:
            return 'success';
        case ECardStatus.OTHER:
        case ECardStatus.WAITING_FOR_ACTIVATION:
        case ECardStatus.CUSTOMER_REQUEST:
        case ECardStatus.TEMPORARY_CLOSED:
            return 'warning';
        case ECardStatus.LOST:
        case ECardStatus.STOLEN:
        case ECardStatus.FRAUD:
        case ECardStatus.CLOSED:
        case ECardStatus.LEGAL_CLOSED:
            return 'error';
        case ECardStatus.FREEZED:
            return 'blue';
    }
};

export const getIntentStatus = (status?: EPaymentIntentStatus) => {
    switch (status) {
        case EPaymentIntentStatus.Paid:
            return 'success';
        case EPaymentIntentStatus.Pending:
            return 'warning';
        case EPaymentIntentStatus.Declined:
            return 'error';
        case EPaymentIntentStatus.InProcess:
            return 'blue';
        case EPaymentIntentStatus.Expired:
        case EPaymentIntentStatus.Canceled:
        case EPaymentIntentStatus.Unknown:
            return 'gray';
        default:
            return 'success';
    }
};

export const getTransactionStatus = (status?: ETransactionStatus) => {
    switch (status) {
        case ETransactionStatus.APPROVED:
            return 'success';
        case ETransactionStatus.PENDING:
            return 'warning';
        case ETransactionStatus.DECLINED:
        case ETransactionStatus.REFUND:
        case ETransactionStatus.CANCELED:
        case ETransactionStatus.ERROR:
            return 'error';
        default:
            return 'success';
    }
};

export const fileToBase64 = (file: File) => {
    const promise = async () =>
        new Promise<string>((res, rej) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                res(reader?.result as string);
            };
            reader.onerror = (error) => {
                rej(`Error reading file: ${error}`);
                console.error('Error reading file: ', error);
            };
        });
    return promise();
};

export const base64ToFile = (base64String: string, fileName: string, mimeType: string) => {
    const byteString = atob(base64String.split(',')[1]);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uint8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
        uint8Array[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([uint8Array], { type: mimeType });
    return new File([blob], fileName, { type: mimeType });
};

export const downloadFileFromBlob = (data: any, name: string) => {
    const a = document.createElement('a');
    a.download = name;
    a.href = URL.createObjectURL(data);
    a.addEventListener('click', () => {
        setTimeout(() => URL.revokeObjectURL(a.href), 30 * 1000);
    });
    a.click();
};

export const formatDataToXlsx = (data: any[]) => {
    if (!data || data.length === 0) {
        throw new Error('Data array cannot be empty');
    }

    // Collect unique keys and ensure consistent formatting
    const keys: string[] = [];
    data.forEach((item: Record<string, any>) => {
        Object.keys(item).forEach((key) => {
            const formattedKey = camelize(key);
            if (!keys.includes(formattedKey)) {
                keys.push(formattedKey);
            }
        });
    });

    // Calculate column widths dynamically
    const widthArray = keys.map((key) => {
        return {
            wch: Math.max(
                key.length, // Account for header name length
                // Get the widest recorded value for the column
                ...data.map((row) => `${row[key] || ''}`.length)
            )
        };
    });

    const fileType =
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';

    // Convert data to worksheet
    const ws = XLSX.utils.json_to_sheet(data);

    // Attach calculated column widths
    ws['!cols'] = widthArray;

    // Create workbook and write it to an XLSX buffer
    const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    return new Blob([excelBuffer], { type: fileType });
};

export const camelize = (str: string) => {
    return str
        .replace(/^\w|[A-Z]|\b\w/g, function (word, index) {
            return index === 0 ? word.toUpperCase() : word.toUpperCase();
        })
        .replace(/\s+/g, '');
};

export const prettifyAmount = (amount: string | number) => {
    const parts = typeof amount === 'string' ? amount.split('.') : amount.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
};

export function getDefaultPhoneCode(tz?: string) {
    const countries = Country.getAllCountries();
    const localTimeZone = tz || moment.tz.guess();
    const defaultCountry = Country.getCountryByCode('CA');

    const localCountry = countries.find((country) =>
        country?.timezones?.find((data) => data.zoneName === localTimeZone)
    );

    return (localCountry?.phonecode as string) || (defaultCountry?.phonecode as string);
}

export const getDateMonthAgo = (): { dateNow: Date; dateMonthAgo: Date } => {
    const dateNow = new Date();
    const dateMonthAgo = new Date();

    // Set the date to a month ago
    dateMonthAgo.setMonth(dateMonthAgo.getMonth() - 1);

    return {
        dateNow,
        dateMonthAgo
    };
};

export const groupNotificationsByMonth = (
    notifications: TNotification[]
): { month: string; notifications: TNotification[] }[] => {
    const grouped = notifications.reduce(
        (acc, notification) => {
            const date = new Date(notification.created_at);
            const monthIndex = date.getMonth(); // Get the month index (0-11)
            const monthName = months[monthIndex];

            if (!acc[monthName]) {
                acc[monthName] = [];
            }

            acc[monthName].push(notification);

            return acc;
        },
        {} as Record<string, TNotification[]>
    );

    return Object.keys(grouped).map((month) => ({
        month,
        notifications: grouped[month]
    }));
};

export const prettifyFileSize = (sizeInBytes: number): string => {
    if (sizeInBytes === 0) return '0 Bytes';

    const units = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const factor = 1024;
    const unitIndex = Math.floor(Math.log(sizeInBytes) / Math.log(factor));
    const prettySize = sizeInBytes / Math.pow(factor, unitIndex);

    // Ensure two decimal places for readability
    return `${prettySize.toFixed(2)} ${units[unitIndex]}`;
};

interface GroupedMessages {
    date: string; // Formatted as DD.MM.YYYY
    messages: TTicketMessage[];
}

export const groupMessagesByDate = (
    messages: TTicketMessage[],
    userTimezone: string // Pass the user's timezone as a parameter
): GroupedMessages[] => {
    const groupedMessages: Record<string, TTicketMessage[]> = {};

    // Group messages by date (adjusted to user's timezone)
    messages.forEach((msg) => {
        const dateKey = formatDate(msg.created_at, 'MM.DD.YYYY'); // Adjust to user's timezone
        if (!groupedMessages[dateKey]) {
            groupedMessages[dateKey] = [];
        }
        groupedMessages[dateKey].push(msg);
    });

    // Convert groupedMessages into an array, sort messages inside each group, and sort groups by date
    return Object.entries(groupedMessages)
        .map(([date, messages]) => ({
            date,
            messages: messages.sort((a, b) => {
                // Sort messages within the group by `created_at` (oldest to newest)
                const timeA = moment(a.created_at).tz(userTimezone).valueOf();
                const timeB = moment(b.created_at).tz(userTimezone).valueOf();
                return timeA - timeB; // Ascending order (oldest to newest)
            })
        }))
        .sort((a, b) => {
            // Sort groups by `date` in ascending order (oldest group first)
            //@ts-ignore
            const dateA = moment(a.date, 'MM.DD.YYYY').tz(userTimezone).valueOf();
            const dateB = moment(b.date, 'MM.DD.YYYY').tz(userTimezone).valueOf();
            return dateA - dateB; // Ascending order
        });
};

export const getTicketStatus = (status: ETicketStatus) => {
    switch (status) {
        case ETicketStatus.NEW:
        case ETicketStatus.OPEN:
        case ETicketStatus.HOLD:
            return ETicketStatus.OPEN;
        default:
            return status;
    }
};

export const getTicketStatusVariant = (status: ETicketStatus) => {
    switch (status) {
        case ETicketStatus.OPEN:
            return 'warning';
        case ETicketStatus.PENDING:
            return 'error';
        case ETicketStatus.SOLVED:
            return 'success';
        case ETicketStatus.CLOSED:
            return 'gray';
        default:
            return 'gray';
    }
};

export const highlightSearchText = (text: string, search: string): string => {
    if (!search) return text; // If there's no search query, return the original text

    const regex = new RegExp(`(${search})`, 'gi'); // Case-insensitive match for the search term
    return text.replace(regex, `<i>$1</i>`); // Wrap matching text in <i> tags
};

export const isIntentPayment = (
    transaction: TTransactionLite | TPaymentIntent
): transaction is TPaymentIntent => {
    return transaction && typeof transaction === 'object' && !('action' in transaction);
};

export const getGiftCardStatusVariant = (status: EGiftCardStatus) => {
    switch (status) {
        case EGiftCardStatus.ACTIVE:
            return 'success';
        case EGiftCardStatus.CANCELLED:
        case EGiftCardStatus.CANCEL_REQUESTED:
            return 'error';
        case EGiftCardStatus.ISSUED:
            return 'blue';
        case EGiftCardStatus.PARTIALLY_REDEEMED:
            return 'lightblue';
        case EGiftCardStatus.REDEEMED:
            return 'gray';
        case EGiftCardStatus.PENDING:
            return 'warning';
        default:
            return 'gray';
    }
};

export const getScheduleStatusVariant = (status: EScheduleStatus) => {
    switch (status) {
        case EScheduleStatus.INACTIVE:
        case EScheduleStatus.ERROR:
            return 'warning';
        case EScheduleStatus.ACTIVE:
            return 'success';
        case EScheduleStatus.COMPLETE:
            return 'gray';
        default:
            return 'gray';
    }
};

export const getFirstLettersBySpace = (string: string) => {
    return string
        .split(' ')
        .map((word) => word[0])
        .join('')
        .toUpperCase();
};

export function getScheduleDescription(
    startedAt: string | Date,
    frequency: EScheduleFrequency
): string {
    // Parse the start date using native Date
    const startDate = new Date(startedAt);

    // Get the day of the week (e.g., "Monday")
    const dayOfWeek = startDate.toLocaleDateString('en-US', { weekday: 'long' });

    // Get the day of the month (e.g., 24)
    const dayOfMonth = startDate.getDate();

    // Get the month name (e.g., "January")
    const month = startDate.toLocaleDateString('en-US', { month: 'long' });

    // Determine the description based on the frequency enum
    switch (frequency) {
        case EScheduleFrequency.DAILY:
            return 'Every day';
        case EScheduleFrequency.WEEKLY:
            return `Every ${dayOfWeek}`;
        case EScheduleFrequency.BIWEEKLY:
            return `Every 2 weeks on ${dayOfWeek}`;
        case EScheduleFrequency.MONTHLY:
            return `Day ${dayOfMonth} on every month`;
        case EScheduleFrequency.QUARTERLY:
            return `Day ${dayOfMonth} on every 3 months`;
        case EScheduleFrequency.SEMIANNUALLY:
            return `Day ${dayOfMonth} on every 6 months`;
        case EScheduleFrequency.ANNUALLY:
            return `Day ${dayOfMonth} of ${month} every year`;
        case EScheduleFrequency.ONCE:
            return `On ${dayOfMonth} ${month} only`;
        default:
            return `One-time schedule on Day ${dayOfMonth}`;
    }
}

export const getScheduleTypeByMethodAndAction = (
    method: ETransactionMethod,
    action: ETransactionAction
) => {
    if (method === ETransactionMethod.WALLET) {
        if (action === ETransactionAction.REQUEST) {
            return ETransactionType.DEBIT;
        } else if (action === ETransactionAction.TRANSFER) {
            return ETransactionType.CREDIT;
        }
    } else if (method === ETransactionMethod.EPS) {
        if (action === ETransactionAction.DEPOSIT) {
            return ETransactionType.DEBIT;
        }
    } else if (method === ETransactionMethod.EFT) {
        if (action === ETransactionAction.DEPOSIT) {
            return ETransactionType.DEBIT;
        } else if (action === ETransactionAction.WITHDRAW) {
            return ETransactionType.CREDIT;
        }
    } else if (method === ETransactionMethod.PAY_BILL) {
        if (action === ETransactionAction.PAYMENT) {
            return ETransactionType.CREDIT;
        }
    }
    return ETransactionType.DEBIT;
};

export function getStartDate({
    nextPaymentAt,
    lastPaymentAt,
    donePayments,
    frequency
}: {
    nextPaymentAt?: Date | string;
    lastPaymentAt?: Date | string;
    donePayments: number;
    frequency: EScheduleFrequency;
}) {
    // Determine which date to use as base
    const baseDate = nextPaymentAt || lastPaymentAt;

    if (!baseDate) {
        throw new Error('Either nextPaymentAt or lastPaymentAt must be provided');
    }

    const referenceDate = new Date(baseDate);

    // Validate the input date
    if (isNaN(referenceDate.getTime())) {
        throw new Error('Invalid date provided');
    }

    // Create a new date object using LOCAL time components to avoid timezone shifts
    const startDate = new Date(referenceDate);

    // If using nextPaymentAt
    if (nextPaymentAt) {
        // If no payments have been done yet, the start date is the next payment date
        if (donePayments === 0) {
            // Use local time components, not UTC
            const year = startDate.getFullYear();
            const month = startDate.getMonth();
            const day = startDate.getDate();
            return new Date(year, month, day, 0, 0, 0, 0).toISOString();
        }

        // Calculate backwards from next payment using LOCAL methods
        const year = startDate.getFullYear();
        const month = startDate.getMonth();
        const day = startDate.getDate();
        const calcDate = new Date(year, month, day, 0, 0, 0, 0);

        switch (frequency) {
            case EScheduleFrequency.ONCE:
                return calcDate.toISOString();

            case EScheduleFrequency.DAILY:
                calcDate.setDate(calcDate.getDate() - donePayments);
                return calcDate.toISOString();

            case EScheduleFrequency.WEEKLY:
                calcDate.setDate(calcDate.getDate() - donePayments * 7);
                return calcDate.toISOString();

            case EScheduleFrequency.BIWEEKLY:
                calcDate.setDate(calcDate.getDate() - donePayments * 14);
                return calcDate.toISOString();

            case EScheduleFrequency.MONTHLY:
                calcDate.setMonth(calcDate.getMonth() - donePayments);
                return calcDate.toISOString();

            case EScheduleFrequency.QUARTERLY:
                calcDate.setMonth(calcDate.getMonth() - donePayments * 3);
                return calcDate.toISOString();

            case EScheduleFrequency.SEMIANNUALLY:
                calcDate.setMonth(calcDate.getMonth() - donePayments * 6);
                return calcDate.toISOString();

            case EScheduleFrequency.ANNUALLY:
                calcDate.setFullYear(calcDate.getFullYear() - donePayments);
                return calcDate.toISOString();

            default:
                throw new Error('Invalid payment frequency');
        }
    } else {
        // If using lastPaymentAt
        const year = startDate.getFullYear();
        const month = startDate.getMonth();
        const day = startDate.getDate();
        const calcDate = new Date(year, month, day, 0, 0, 0, 0);

        // Calculate backwards from last payment (which is one payment before the next)
        const paymentsToGoBack = donePayments > 0 ? donePayments - 1 : 0;

        switch (frequency) {
            case EScheduleFrequency.ONCE:
                return calcDate.toISOString();

            case EScheduleFrequency.DAILY:
                calcDate.setDate(calcDate.getDate() - paymentsToGoBack);
                return calcDate.toISOString();

            case EScheduleFrequency.WEEKLY:
                calcDate.setDate(calcDate.getDate() - paymentsToGoBack * 7);
                return calcDate.toISOString();

            case EScheduleFrequency.BIWEEKLY:
                calcDate.setDate(calcDate.getDate() - paymentsToGoBack * 14);
                return calcDate.toISOString();

            case EScheduleFrequency.MONTHLY:
                calcDate.setMonth(calcDate.getMonth() - paymentsToGoBack);
                return calcDate.toISOString();

            case EScheduleFrequency.QUARTERLY:
                calcDate.setMonth(calcDate.getMonth() - paymentsToGoBack * 3);
                return calcDate.toISOString();

            case EScheduleFrequency.SEMIANNUALLY:
                calcDate.setMonth(calcDate.getMonth() - paymentsToGoBack * 6);
                return calcDate.toISOString();

            case EScheduleFrequency.ANNUALLY:
                calcDate.setFullYear(calcDate.getFullYear() - paymentsToGoBack);
                return calcDate.toISOString();

            default:
                throw new Error('Invalid payment frequency');
        }
    }
}

// Helper function to apply timezone adjustment
// function applyTimezoneAdjustment(date: Date): Date {
//     // Get the timezone offset in minutes (negative for ahead of UTC, positive for behind UTC)
//     const timezoneOffset = date.getTimezoneOffset();

//     // If timezone offset is positive (behind UTC), add 1 day
//     // Note: getTimezoneOffset() returns positive values for timezones behind UTC
//     // and negative values for timezones ahead of UTC
//     if (timezoneOffset > 0) {
//         const adjustedDate = new Date(date);
//         adjustedDate.setDate(adjustedDate.getDate() + 1);
//         return adjustedDate;
//     }

//     // If timezone offset is 0 or negative (UTC or ahead of UTC), return unchanged
//     return date;
// }

/**
 * Get the frequency interval in days based on the schedule frequency.
 *
 * @param frequency - The schedule frequency as an enum of EScheduleFrequency.
 * @returns The interval in days for the given frequency.
 */
export function getFrequencyIntervalInDays(frequency: EScheduleFrequency): number {
    switch (frequency) {
        case EScheduleFrequency.ONCE:
            return 0;
        case EScheduleFrequency.DAILY:
            return 1;
        case EScheduleFrequency.WEEKLY:
            return 7;
        case EScheduleFrequency.BIWEEKLY:
            return 14;
        case EScheduleFrequency.MONTHLY:
            return 30; // Approximately, assumes 30-day months
        case EScheduleFrequency.QUARTERLY:
            return 90; // Approximately, assumes 3 months of 30 days each
        case EScheduleFrequency.SEMIANNUALLY:
            return 180; // Approximate, 6 months * 30 days
        case EScheduleFrequency.ANNUALLY:
            return 365;
        default:
            throw new Error('Invalid frequency provided.');
    }
}

export function calculateEndDate(
    frequency: EScheduleFrequency,
    nextPayment?: string | Date,
    lastPayment?: string | Date,
    remaining_payments: number = 0
): Date {
    // Determine the base date to calculate from
    const baseDate = nextPayment
        ? moment(nextPayment) // If the next payment exists, use it as the base
        : moment(lastPayment); // Otherwise, use last payment

    // If no base date is provided, throw an error
    if (!baseDate) {
        throw new Error('Either nextPayment or lastPayment must be provided.');
    }

    // Calculate the frequency interval in the past days
    const frequencyInterval = getFrequencyIntervalInDays(frequency);

    // Calculate the end date by adding the interval * remaining_payments
    const endDate = baseDate.clone().add(remaining_payments * frequencyInterval, 'days');

    return endDate.toDate();
}

export const copyStringToClipboard = (text: string) => {
    navigator.clipboard
        .writeText(text)
        .then(() => {
            showToast.success('Copied to clipboard');
        })
        .catch((err) => {
            console.error('Failed to copy text: ', err);
        });
};

export const getLoyaltyLevelString = (level?: ELoyaltyLevel) => {
    switch (level) {
        case ELoyaltyLevel.A:
            return 'Silver';
        case ELoyaltyLevel.B:
            return 'Gold';
        case ELoyaltyLevel.C:
            return 'Platinum';
        default:
            return 'Gold';
    }
};

export const mapOccupationsToOptions = () => {
    return Object.keys(occupation).map((item) => {
        return {
            label: occupation[item],
            value: item
        };
    });
};

export const fixedNumbers = (num?: number): string => {
    if (num === undefined) return '';
    const rounded = Math.round(num * 100) / 100;
    const formatted = rounded.toFixed(2);
    return formatted.replace(/\.?0+$/, '');
};

export const getFundsOperation = (item: TTransaction | TTransactionLite) => {
    return `${item.method}_${item.type}_${item.action}` as EFundsOperation;
};
/**
 * Format a date according to the specified format string
 * @param date - Date object, string, or timestamp
 * @param format - Format string with tokens
 * @param locale - Locale for month/day names (default: 'en-US')
 * @returns Formatted date string
 */
export function formatDate(
    inputDate?: Date | string | number,
    format?: string,
    locale: string = 'en-US'
): string {
    if (!inputDate || !format) return '';
    const d = inputDate ? new Date(inputDate) : new Date();

    // A truthy-but-unparseable value (e.g. a malformed date string in an API
    // response) degrades to the same "nothing to show" empty string as the
    // missing-input guard above, rather than throwing. Every call site in
    // this app renders the result directly inline in JSX with no
    // try/catch, and there is no ErrorBoundary anywhere in this app either
    // — a thrown error here has nothing to stop it and takes down the
    // entire page, not just the one value that failed to format.
    if (isNaN(d.getTime())) return '';

    // Check if date represents midnight in UTC (00:00:00.000Z)
    const isUTCMidnight =
        d.getUTCHours() === 0 &&
        d.getUTCMinutes() === 0 &&
        d.getUTCSeconds() === 0 &&
        d.getUTCMilliseconds() === 0;

    // Use local time (respect timezone) unless date represents UTC midnight
    const useLocalTime = !isUTCMidnight;
    const timeZone = useLocalTime ? undefined : 'UTC';

    // Helper functions to get date parts (local or UTC)
    const getYear = () => (useLocalTime ? d.getFullYear() : d.getUTCFullYear());
    const getMonth = () => (useLocalTime ? d.getMonth() : d.getUTCMonth());
    const getDate = () => (useLocalTime ? d.getDate() : d.getUTCDate());
    const getDay = () => (useLocalTime ? d.getDay() : d.getUTCDay());
    const getHours = () => (useLocalTime ? d.getHours() : d.getUTCHours());
    const getMinutes = () => (useLocalTime ? d.getMinutes() : d.getUTCMinutes());
    const getSeconds = () => (useLocalTime ? d.getSeconds() : d.getUTCSeconds());
    const getMilliseconds = () => (useLocalTime ? d.getMilliseconds() : d.getUTCMilliseconds());

    // Get localized month and weekday names
    const monthsLong = Array.from({ length: 12 }, (_, i) => {
        const referenceDate = useLocalTime ? new Date(2000, i, 1) : new Date(Date.UTC(2000, i, 1));

        return new Intl.DateTimeFormat(locale, {
            month: 'long',
            ...(timeZone && { timeZone })
        }).format(referenceDate);
    });

    const monthsShort = Array.from({ length: 12 }, (_, i) => {
        const referenceDate = useLocalTime ? new Date(2000, i, 1) : new Date(Date.UTC(2000, i, 1));

        return new Intl.DateTimeFormat(locale, {
            month: 'short',
            ...(timeZone && { timeZone })
        }).format(referenceDate);
    });

    const weekdaysLong = Array.from({ length: 7 }, (_, i) => {
        const referenceDate = useLocalTime
            ? new Date(2000, 0, 2 + i)
            : new Date(Date.UTC(2000, 0, 2 + i));

        return new Intl.DateTimeFormat(locale, {
            weekday: 'long',
            ...(timeZone && { timeZone })
        }).format(referenceDate);
    });

    const weekdaysShort = Array.from({ length: 7 }, (_, i) => {
        const referenceDate = useLocalTime
            ? new Date(2000, 0, 2 + i)
            : new Date(Date.UTC(2000, 0, 2 + i));

        return new Intl.DateTimeFormat(locale, {
            weekday: 'short',
            ...(timeZone && { timeZone })
        }).format(referenceDate);
    });

    // Get day of year
    const startOfYear = useLocalTime
        ? new Date(getYear(), 0, 1)
        : new Date(Date.UTC(getYear(), 0, 1));
    const dayOfYear = Math.floor((d.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000)) + 1;

    // Get week of year (ISO week)
    const firstThursday = useLocalTime
        ? new Date(getYear(), 0, 1)
        : new Date(Date.UTC(getYear(), 0, 1));

    if (useLocalTime) {
        firstThursday.setDate(firstThursday.getDate() + ((4 - firstThursday.getDay() + 7) % 7));
    } else {
        firstThursday.setUTCDate(
            firstThursday.getUTCDate() + ((4 - firstThursday.getUTCDay() + 7) % 7)
        );
    }

    const weekOfYear =
        Math.floor((d.getTime() - firstThursday.getTime()) / (7 * 24 * 60 * 60 * 1000)) + 1;

    // Get quarter
    const quarter = Math.ceil((getMonth() + 1) / 3);

    // Calculate timezone offset
    let timezoneOffsetHours = 0;
    let timezoneOffsetMinutes = 0;
    let timezoneSign = '+';

    if (useLocalTime) {
        const offsetMinutes = d.getTimezoneOffset();
        timezoneOffsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
        timezoneOffsetMinutes = Math.abs(offsetMinutes) % 60;
        timezoneSign = offsetMinutes <= 0 ? '+' : '-';
    }
    // For UTC midnight, timezone is always +00:00
    // (timezoneOffsetHours and timezoneOffsetMinutes remain 0, timezoneSign remains '+')

    // Get timezone tokens
    const timezoneZ = `${timezoneSign}${String(timezoneOffsetHours).padStart(2, '0')}:${String(timezoneOffsetMinutes).padStart(2, '0')}`;
    const timezoneZZ = `${timezoneSign}${String(timezoneOffsetHours).padStart(2, '0')}${String(timezoneOffsetMinutes).padStart(2, '0')}`;

    // Token definitions - order is crucial, longest patterns first
    const tokenDefinitions = [
        ['YYYY', getYear().toString()],
        ['MMMM', monthsLong[getMonth()]],
        ['dddd', weekdaysLong[getDay()]],
        ['DDDD', String(dayOfYear).padStart(3, '0')],
        ['DDDo', getOrdinal(dayOfYear)],
        ['MMM', monthsShort[getMonth()]],
        ['ddd', weekdaysShort[getDay()]],
        ['DDD', String(dayOfYear)],
        ['SSS', String(getMilliseconds()).padStart(3, '0')],
        ['YY', getYear().toString().slice(-2)],
        ['MM', String(getMonth() + 1).padStart(2, '0')],
        ['Mo', getOrdinal(getMonth() + 1)],
        ['DD', String(getDate()).padStart(2, '0')],
        ['Do', getOrdinal(getDate())],
        ['ww', String(weekOfYear).padStart(2, '0')],
        ['wo', getOrdinal(weekOfYear)],
        ['dd', weekdaysShort[getDay()].slice(0, 2)],
        ['do', getOrdinal(getDay())],
        ['HH', String(getHours()).padStart(2, '0')],
        ['hh', String(getHours() % 12 || 12).padStart(2, '0')],
        ['kk', String(getHours() + 1).padStart(2, '0')],
        ['mm', String(getMinutes()).padStart(2, '0')],
        ['ss', String(getSeconds()).padStart(2, '0')],
        ['SS', String(Math.floor(getMilliseconds() / 10)).padStart(2, '0')],
        ['ZZ', timezoneZZ],
        ['Qo', getOrdinal(quarter)],
        ['Y', getYear().toString()],
        ['M', String(getMonth() + 1)],
        ['D', String(getDate())],
        ['Q', quarter.toString()],
        ['w', String(weekOfYear)],
        ['d', String(getDay())],
        ['E', String(getDay() === 0 ? 7 : getDay())],
        ['H', String(getHours())],
        ['h', String(getHours() % 12 || 12)],
        ['k', String(getHours() + 1)],
        ['m', String(getMinutes())],
        ['s', String(getSeconds())],
        ['S', String(Math.floor(getMilliseconds() / 100))],
        ['A', getHours() >= 12 ? 'PM' : 'AM'],
        ['a', getHours() >= 12 ? 'pm' : 'am'],
        ['Z', timezoneZ],
        ['X', Math.floor(d.getTime() / 1000).toString()],
        ['x', d.getTime().toString()]
    ];

    // Safe token replacement using position tracking
    let result = '';
    let i = 0;

    while (i < format.length) {
        let matched = false;

        // Try to match tokens starting from current position
        for (let j = 0; j < tokenDefinitions.length; j++) {
            const [token, value] = tokenDefinitions[j];

            if (format.substr(i, token.length) === token) {
                result += value;
                i += token.length;
                matched = true;
                break;
            }
        }

        // If no token matched, copy the character as-is
        if (!matched) {
            result += format[i];
            i++;
        }
    }

    return result;
}

/**
 * Helper function to get ordinal suffix (1st, 2nd, 3rd, etc.)
 */
function getOrdinal(num: number): string {
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const value = num % 100;
    const suffix = suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0];
    return num + suffix;
}
